import React from 'react';
import { FileText, Upload, Loader2 } from 'lucide-react';
import type { Theme } from '../../themes/theme';

import type { ColumnConfig } from '../../hooks/useColumnManager';
import type { TableItem } from '../../services/driveService';
import type { ApiDocument, PDFSnippet, SavedMessage } from '../../services/api';
import type { SavedContent } from '../../services/savedContentService';
import DriveTableRow from './DriveTableRow';

export interface DriveTableBodyProps {
  theme: Theme;
  visibleColumns: ColumnConfig[];
  tableItems: TableItem[];
  internalSelectedDocuments: string[];
  handleDocumentToggle: (id: string) => void;
  handleItemOpen: (item: TableItem) => void;
  renderCellContent: (
    column: ColumnConfig,
    item: TableItem,
    processingSummary?: string
  ) => React.ReactNode;
  currentUser: any;
  formatProcessingTime: (value?: number) => string | undefined;
  formatProcessingMethods: (methods?: string[]) => string | undefined;
  isLoadingPrivate: boolean;
  isLoadingShared: boolean;
  openMenuId: string | null;
  setOpenMenuId: (id: string | null) => void;
  menuPosition: Record<string, { top?: number; bottom?: number; left: number }>;
  menuRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  menuButtonRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  onDocumentReprocess?: (documentId: string) => void;
  onDocumentDelete?: (doc: ApiDocument) => Promise<void> | void;
  onViewConceptualSummary?: (doc: ApiDocument) => void;
  onSnippetDelete?: (snippet: PDFSnippet) => Promise<void> | void;
  onSavedMessageDelete?: (message: SavedMessage) => Promise<void> | void;
  onStudioResultDelete?: (result: SavedContent) => Promise<void> | void;
}

const DriveTableBody: React.FC<DriveTableBodyProps> = ({
  theme,
  visibleColumns,
  tableItems,
  internalSelectedDocuments,
  handleDocumentToggle,
  handleItemOpen,
  renderCellContent,
  currentUser,
  formatProcessingTime,
  formatProcessingMethods,
  isLoadingPrivate,
  isLoadingShared,
  openMenuId,
  setOpenMenuId,
  menuPosition,
  menuRefs,
  menuButtonRefs,
  onDocumentReprocess,
  onDocumentDelete,
  onViewConceptualSummary,
  onSnippetDelete,
  onSavedMessageDelete,
  onStudioResultDelete,
}) => {
  return (
    <div className="min-w-max">
      {(isLoadingPrivate || isLoadingShared) && tableItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 animate-fade-in">
          <Loader2 className={`w-6 h-6 ${theme.colors.text.muted} animate-spin mb-3`} />
          <span className={`text-sm ${theme.colors.text.secondary}`}>Cargando documentos...</span>
        </div>
      ) : tableItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 animate-empty-state">
          <div className="w-12 h-12 rounded-2xl bg-nb-accent/10 flex items-center justify-center mb-4">
            <FileText className="w-6 h-6 text-nb-accent" />
          </div>
          <h3 className={`text-sm font-medium ${theme.colors.text.primary} mb-1`}>
            No hay documentos
          </h3>
          <p className={`text-xs ${theme.colors.text.secondary} max-w-xs text-center`}>
            Sube tu primer PDF para empezar a analizar documentos financieros.
          </p>
          <div className={`flex items-center gap-1.5 mt-4 text-xs ${theme.colors.text.muted}`}>
            <Upload className="w-3.5 h-3.5" />
            <span>Arrastra archivos o usa el boton de subir</span>
          </div>
        </div>
      ) : (
        tableItems.map((item) => {
          const processingTimeLabel = formatProcessingTime(item.processingTimeSeconds ?? undefined);
          const processingMethodsLabel = formatProcessingMethods(item.processingMethods);
          const processingSummary =
            processingTimeLabel && processingMethodsLabel
              ? `Procesado en ${processingTimeLabel} • Métodos: ${processingMethodsLabel}`
              : processingTimeLabel
                ? `Procesado en ${processingTimeLabel}`
                : processingMethodsLabel
                  ? `Métodos: ${processingMethodsLabel}`
                  : undefined;

          return (
            <DriveTableRow
              key={item.id}
              theme={theme}
              item={item}
              visibleColumns={visibleColumns}
              isSelected={internalSelectedDocuments.includes(item.id)}
              handleDocumentToggle={handleDocumentToggle}
              handleItemOpen={handleItemOpen}
              renderCellContent={renderCellContent}
              currentUser={currentUser}
              processingSummary={processingSummary}
              openMenuId={openMenuId}
              setOpenMenuId={setOpenMenuId}
              menuPosition={menuPosition}
              menuRefs={menuRefs}
              menuButtonRefs={menuButtonRefs}
              onDocumentReprocess={onDocumentReprocess}
              onDocumentDelete={onDocumentDelete}
              onViewConceptualSummary={onViewConceptualSummary}
              onSnippetDelete={onSnippetDelete}
              onSavedMessageDelete={onSavedMessageDelete}
              onStudioResultDelete={onStudioResultDelete}
            />
          );
        })
      )}
    </div>
  );
};

export default DriveTableBody;
